// ---------------------------------------------------------------------------
// Generation timing budgets (ARC-30, 2026-08-13)
//
// One place for every "how long are we willing to wait for BCE" number, on
// both halves of the codebase. It exists because the previous caps were spread
// across four files and drifted out of agreement: ARC-4 doubled one of them
// (server/blacksmith.ts generateQuestions), and the path it doubled was one
// batch generation had already stopped using, so the symptom came straight
// back. Caps that live apart drift apart.
//
// EVERY constant below traces to a recorded observation, not to a round
// number someone liked. When you change one, change the observation it cites
// or add a new one — a budget with no measurement behind it is the bug this
// module exists to stop being re-introduced.
//
// THE OBSERVATIONS
//
//  O1. 50s per item, end to end, including CDE duplicate-detection regen.
//      A 20-task batch measured 990s in production on 2026-07-05
//      (990 / 20 = 49.5s). Independently corroborated on 2026-07-20 by a live
//      10-question session on archaine-waggle that was still healthy and
//      working at ~495s (495 / 10 = 49.5s) when the then-current client
//      deadline killed it.
//
//  O2. 25-50s for a single question, before any repair pass — the figure this
//      repo's own client comments have carried since 2026-07-06. A CDE dedup
//      or SOCRATES repair re-runs the task, so an item that needs one costs
//      roughly twice its first-pass time. O1's 49.5s average is a batch figure
//      that already includes those repairs, which is why it sits at the top of
//      O2's range rather than above it.
//
//  O3. The finalizing phase (all tasks reported complete, BCE still running
//      the whole-batch CDE pass) takes 10-15 minutes and does NOT shrink with
//      test size — the ARC-26 field report describes it on a small test, and
//      the 990s in O1 is a 20-task batch. It therefore gets a flat budget, not
//      a per-item one. Folding it into a per-item multiplier is what let a
//      3-item test have less total budget than the finalizing pass alone
//      needs.
//
// THE SAFETY MULTIPLE
//
//  A budget is not an estimate. The cost of waiting too long is a user
//  watching a progress bar that is honestly reporting progress; the cost of
//  giving up too early is throwing away a completed generation and burning a
//  duplicate one — which is what the ARC-30 report is. So observations are
//  multiplied by 3 before they become a ceiling, and the ceiling is a
//  last-resort backstop rather than a schedule.
// ---------------------------------------------------------------------------

/** O1: measured seconds per item for a batch, including CDE regeneration. */
export const OBSERVED_SECONDS_PER_ITEM = 50;

/**
 * O2 upper bound, doubled to cover one repair pass. The cost of a single item
 * generated on its own ("Regenerate item", "Add item"), which has no batch to
 * amortise submission against.
 */
export const OBSERVED_SINGLE_ITEM_SECONDS = 50 * 2;

/**
 * O3: the flat whole-batch finalizing pass. Upper end of the observed 10-15
 * minute range; it gets its own budget rather than a share of the per-item
 * one.
 */
export const OBSERVED_FINALIZING_SECONDS = 15 * 60;

/**
 * Submission, session bookkeeping and the first status poll — everything
 * between the user's click and BCE reporting on task 1. Not per-item.
 */
export const SUBMISSION_OVERHEAD_SECONDS = 120;

/** See "THE SAFETY MULTIPLE" above. */
export const SAFETY_MULTIPLE = 3;

/**
 * The finalizing pass gets 2x rather than 3x. The 3x on per-item work covers
 * variance compounding across N independent items; finalizing is ONE
 * whole-batch operation observed within a narrow 10-15 minute band, so the
 * same headroom argument does not apply to it. 2x the upper end of that band
 * is 30 minutes, already more generous than the 20 the ARC-26 fix granted it.
 */
export const FINALIZING_SAFETY_MULTIPLE = 2;

/**
 * The client watchdog formula this replaces (300s + 120s/item). Kept as a hard
 * floor.
 *
 * NOTHING OBSERVED SAYS THE OLD CEILING WAS TOO GENEROUS. Every report behind
 * ARC-4, ARC-26 and ARC-30 is the opposite — a budget that expired while the
 * server was healthy and still working. So a re-derivation is only ever allowed
 * to move a budget UP. Without this floor the honest per-item arithmetic below
 * would quietly TIGHTEN small tests (a 3-item test: 570s derived vs 660s
 * before), and small tests are exactly the ones the ARC-26 field report was
 * about. A derivation that makes the reported bug worse for the affected case
 * is a derivation with a mistake in it, whether or not the mistake is visible.
 */
function previousClientCeilingSeconds(items: number): number {
  return 300 + items * 120;
}

/**
 * Ceiling for the GENERATING phase of a batch — up to the point every task
 * reports complete. The finalizing phase that follows gets
 * `finalizingBudgetSeconds()` on top of this, granted when that phase is first
 * observed.
 *
 * Derived part is 120s + 150s/item (submission overhead, plus the measured
 * 50s/item at 3x). Floored by the previous formula, so: 1 item -> 7 min,
 * 3 -> 11, 5 -> 15, 10 -> 27, 20 -> 52. The old numbers covered the WHOLE run
 * including finalizing, which is why a small test could exhaust its budget
 * inside a finalizing pass that had not yet started.
 */
export function batchGeneratingCeilingSeconds(items: number): number {
  const tasks = Math.max(1, Math.floor(items) || 1);
  const derived = SUBMISSION_OVERHEAD_SECONDS + tasks * OBSERVED_SECONDS_PER_ITEM * SAFETY_MULTIPLE;
  return Math.max(derived, previousClientCeilingSeconds(tasks));
}

/**
 * Budget for the finalizing phase, granted once when that phase is first seen.
 * Flat by design — see O3.
 */
export function finalizingBudgetSeconds(): number {
  return OBSERVED_FINALIZING_SECONDS * FINALIZING_SAFETY_MULTIPLE;
}

/**
 * Ceiling refined by what THIS deployment has actually recorded.
 *
 * `observedSeconds` is the mean completed-session duration for tests of the
 * same size from `examflow.generation_sessions` (see
 * /api/generation-time-estimate) — real durations from this install, which is
 * the strongest evidence available and beats any constant in this file. It is
 * used only when it argues for waiting LONGER: a fast week of small tests must
 * not be able to tighten the ceiling below the observation-derived floor, or
 * one quiet Monday silently re-creates this bug.
 *
 * Note that a recorded session duration covers the finalizing pass too, which
 * then gets its own budget on top. That double-counts, in the generous
 * direction, on purpose: see the note on the safety multiple above.
 */
export function refinedGeneratingCeilingSeconds(
  items: number,
  observedSeconds: number | null | undefined,
): number {
  const floor = batchGeneratingCeilingSeconds(items);
  if (!observedSeconds || !Number.isFinite(observedSeconds) || observedSeconds <= 0) return floor;
  return Math.max(floor, Math.ceil(observedSeconds * SAFETY_MULTIPLE));
}

/**
 * Ceiling for one item generated on its own, used by "Regenerate item" and
 * "Add item". 300s.
 *
 * The value this replaces was 90s (30 polls x 3s), against an operation this
 * repo's own comments describe as taking 25-50s before any repair pass — a
 * ceiling under 2x the baseline cost of the thing it bounds, which is not a
 * backstop, it is a coin flip. ARC-4 never touched it.
 */
export const SINGLE_ITEM_CEILING_SECONDS = OBSERVED_SINGLE_ITEM_SECONDS * SAFETY_MULTIPLE;

/**
 * How long a batch may report no forward movement before we SAY SO. This is
 * not a deadline and nothing fails when it passes — it only swaps a silent
 * spinner for an honest "this hasn't advanced in N minutes, here's what you
 * can do". Deliberately not wired to a failure path: a stalled-looking client
 * is a reporting problem, and killing a healthy generation to fix a reporting
 * problem is the trade this issue is about.
 */
export const NO_PROGRESS_NOTICE_SECONDS = OBSERVED_SECONDS_PER_ITEM * SAFETY_MULTIPLE;

/** Minimum recorded sessions before this deployment's own timings are trusted. */
export const MIN_ESTIMATE_SAMPLES = 2;

/**
 * Bound on the recorded-duration estimate lookup in the /start response path.
 *
 * O4: the client aborts /start after 60s (START_TIMEOUT_MS in
 * client/src/lib/generationRequests.ts) and treats a timeout as a failed
 * start with no session id to reattach to, while BCE is already running the
 * paid generation. The estimate lookup runs up to four aggregate queries and
 * the pg pool sets no query timeout (server/db.ts), so a stalled query would
 * otherwise hold the /start response forever — a duplicate generation on the
 * user's retry, the exact failure class this issue removes. 5s is 12x under
 * the client's 60s deadline and far above what a few aggregates over
 * generation_sessions take on a healthy install.
 */
export const ESTIMATE_LOOKUP_TIMEOUT_MS = 5_000;

/** "about 12 minutes" / "about 45 seconds" for user-facing copy. */
export function describeDuration(seconds: number): string {
  const s = Math.max(1, Math.round(seconds));
  if (s < 60) return `about ${Math.max(15, Math.round(s / 15) * 15)} seconds`;
  const minutes = Math.max(1, Math.round(s / 60));
  return `about ${minutes} minute${minutes === 1 ? "" : "s"}`;
}
